import { Injectable } from '@angular/core';
import { Observable, ReplaySubject, combineLatest } from 'rxjs';
import { first, map } from 'rxjs/operators';
import { ITab2 } from '../classes/report-state';

import { HttpClient } from '@angular/common/http';
import { UserDataService } from '@app/discoverer/app-core/user-data.service';
import { BaseController } from '../base-controller';
import { IColumnSetting } from '../classes';
import { Factory } from '../core-data-services';
import { AppSettingsService, IApp } from './app-settings.service';

@Injectable()
export class TabSettingsService extends BaseController {

    public tabKey: string = '';
    public appKey: string = '';
    public oCurrentTab: Observable<ITab2>;
    public oColumnSettings: Observable<IColumnSetting[]>;
    public oColumnDictionary: Observable<{ [key: string]: IColumnSetting }>;
    public oDefaultColumns: Observable<IColumnSetting[]>;
    public oDefaultFacetColumns: Observable<IColumnSetting[]>;
    public oDefaultSorts: Observable<{ dir: string, field: IColumnSetting }[]>;

    private $currentTab = new ReplaySubject<ITab2>(1);
    private $columnSettings = new ReplaySubject<IColumnSetting[]>(1);

    constructor(
        private httpClient: HttpClient,
        private userDataService: UserDataService,
        private appSettings: AppSettingsService) {
        super();

        this.oCurrentTab = this.$currentTab.asObservable();
        this.oColumnSettings = this.$columnSettings.asObservable();
        this.oDefaultColumns = this.$columnSettings.asObservable();
        this.oColumnDictionary = this.$columnSettings.pipe(map(t => {
            const dictionary: { [key: string]: IColumnSetting } = {};
            t.forEach(setting => dictionary[setting.fieldName] = setting);
            return dictionary;
        }));
        this.oDefaultFacetColumns = combineLatest([this.oColumnDictionary, this.oCurrentTab])
            .pipe(map(
                d => {
                    const dictionary = d[0];
                    const tab = d[1];
                    return tab.defaultFacets.map(field => dictionary[field]);
                }
            ));
        this.oDefaultSorts = combineLatest([this.oColumnDictionary, this.oCurrentTab])
            .pipe(map(
                d => {
                    const dictionary = d[0];
                    const tab = d[1];
                    return tab.defaultSorts.map(sortField => ({ dir: sortField.dir, field: dictionary[sortField.sortField] }));
                }
            ));

        this.whenCurrentTabIsSetLoadColumns();
    }

    public async setCurrentTab(tabKey: string, appKey?: string) {
        this.tabKey = tabKey;
        this.appKey = appKey || this.appKey;
        if (appKey) {
            await this.appSettings.setActiveApp(appKey);
        }
        const app = await this.appSettings.getCurrentApp();
        const currentTab = this.getCachedTab(app, tabKey);
        const tab = !currentTab ? await this.fetchTab(appKey || app.key, tabKey) : currentTab;
        this.$currentTab.next(tab);
    }

    private getCachedTab(app: IApp, tabKey: string): ITab2 | undefined {
        const appTabs = this.appSettings.getCachedTabs(app);
        return appTabs?.find(t => t.key === tabKey);
    }

    public async fetchTab(appKey: string, tabKey: string) {
        const tab = (await this.httpClient.get(`/api/es/apps/${appKey}/tabs/${tabKey}`).toPromise()) as ITab2;
        const app = await this.httpClient.get<IApp>(`/api/es/apps/${appKey}`).toPromise();
        tab.app = app;
        await this.appSettings.cacheTab(tab);
        return tab;
    }

    public async getCurrentTab(): Promise<ITab2> {
        return this.oCurrentTab.pipe(first()).toPromise();
    }

    public async getAllColumns(): Promise<IColumnSetting[]> {
        const cols = await this.oColumnSettings.pipe(first()).toPromise();
        return this.filterByPermission(cols);
    }

    public async getDefaultColumns(): Promise<IColumnSetting[]> {
        const cols = await this.oDefaultColumns.pipe(first()).toPromise();
        return this.filterByPermission(cols);
    }

    public async getColumnDictionary(): Promise<{ [key: string]: IColumnSetting }> {
        const cols = await this.getAllColumns();
        const dictionary: { [key: string]: IColumnSetting } = {};
        cols.forEach(col => dictionary[col.fieldName] = col);
        return dictionary;
    }

    public async getDefaultFacetColumns(): Promise<IColumnSetting[]> {
        return this.oDefaultFacetColumns.pipe(first()).toPromise();
    }

    public async getExcelDataMapper(): Promise<Factory<any>> {
        return { create: (row) => row.source };
    }

    public async translateToColumnList(fields: string[]): Promise<IColumnSetting[]> {
        const dictionary = await this.getColumnDictionary();
        return fields.map(f => dictionary[f]);
    }

    private whenCurrentTabIsSetLoadColumns() {
        this.subscriptions.push(
            this.oCurrentTab.subscribe(async currentTab => {
                const columns = await this.getColumns(currentTab);
                const filteredColumns = await this.filterByPermission(columns);
                filteredColumns.forEach(col => {
                    if (col.isDynamicFacet === false) {
                        col.type = 'text';
                    } else {
                        col.type = this.translateType(col);
                    }
                    if (!!col.referenceLink) {
                        col.dataType = 'link';
                    }
                });
                this.$columnSettings.next(filteredColumns);
            })
        );
    }

    translateType(col: IColumnSetting) {
        const translateDataType = {
            string: 'text',
            int: 'numeric',
            integer: 'numeric',
            double: 'numeric',
            number: 'numeric',
            currency: 'numeric',
            percent: 'numeric',
            long: 'numeric',
            date: 'date',
            text: 'text',
            string_array: 'text',
            string_set: 'text',
            timestamp: 'date',
            timestamptz: 'date',
            link: 'text',
            phone: 'text',
            boolean: 'boolean'
        };
        return translateDataType[col.dataType];
    }

    public async getTabColumns(currentTab: ITab2): Promise<IColumnSetting[]> {
        const cols = await this.httpClient.get<IColumnSetting[]>(`/api/es/apps/${currentTab.app.key}/tabs/${currentTab.key}/columns`).toPromise();
        const filteredCols = await this.filterByPermission(cols.map(col => ({
            ...col,
            type: this.translateType(col)
        })));
        console.log('mapped columns', filteredCols);
        return filteredCols;
    }

    public async updateTabColumns(tabId: string) {
        const app = await this.appSettings.getCurrentAppTabs();
        const currentTab = app.find(tab => tab.key == tabId);
        const freshCols = await this.httpClient.get<IColumnSetting[]>(`/api/es/apps/${currentTab.app?.key}/tabs/${currentTab.key}/columns`).toPromise();
        const filtered = await this.filterByPermission(freshCols);

        const cacheKey = `${currentTab.app?.key}_${currentTab?.key}`;
        _tabSettingsCache[cacheKey] = filtered;

        this.$columnSettings.next(filtered);
        return filtered;
    }

    private async getColumns(currentTab: ITab2) {
        const cacheKey = `${currentTab.app.key}_${currentTab.key}`;
        if (!_tabSettingsCache[cacheKey]) {
            _tabSettingsCache[cacheKey] = await this.httpClient.get<IColumnSetting[]>(
                `/api/es/apps/${currentTab.app?.key}/tabs/${currentTab.key}/columns`
            ).toPromise();
        }
        return _tabSettingsCache[cacheKey];
    }

    private async filterByPermission(columns: IColumnSetting[]): Promise<IColumnSetting[]> {
        const user = await this.userDataService.getUser();
        return columns.filter(col =>
            !col.permission || user.permissions?.includes(col.permission)
        );
    }
}

const _tabSettingsCache: { [appTabKey: string]: IColumnSetting[] } = {};